import { useState, useMemo } from 'react'
import { useApp } from '@/context/AppContext'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import { printCalendar } from '@/utils/printUtils'
import { RECURRENCE_OPTIONS, generateRecurringDates, toYMD } from '@/utils/dateUtils'

// ── Event type config ─────────────────────────────────────────────────────────
const EVENT_TYPES = {
  'custody-exchange': { label: '🔄 Custody Exchange',  dot: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700',    cell: 'bg-blue-50'   },
  'appointment':      { label: '🏥 Appointment',        dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700', cell: 'bg-purple-50' },
  'school':           { label: '🎓 School Event',       dot: 'bg-green-500',  badge: 'bg-green-100 text-green-700',  cell: 'bg-green-50'  },
  'court':            { label: '⚖️ Court Hearing',       dot: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700',  cell: 'bg-amber-50'  },
  'compliance':       { label: '📋 Order Compliance',   dot: 'bg-red-500',    badge: 'bg-red-100 text-red-700',      cell: 'bg-red-50'    },
  'other':            { label: '📌 Other',               dot: 'bg-slate-400',  badge: 'bg-slate-100 text-slate-600',  cell: 'bg-slate-100' },
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

const EMPTY_FORM = {
  title: '', type: 'custody-exchange', start_date: '', start_time: '',
  end_date: '', end_time: '', location: '', notes: '', visibility: 'shared', child_id: '',
  recurring: false, recurrence: 'weekly', recurrenceEnd: '',
}


function canDeleteEvent(event, userId) {
  return event.created_by === userId || event.ownership === 'own'
}

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days = []
  for (let i = 0; i < firstDay; i++) days.push(null)
  for (let d = 1; d <= daysInMonth; d++) days.push(d)
  return days
}

// ── Add event modal ───────────────────────────────────────────────────────────
function AddEventModal({ onClose, onSave, defaultDate, children }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, start_date: defaultDate || '' })
  const [loading, setLoading] = useState(false)
  const [dateError, setDateError] = useState('')
  const set = (k, v) => { setDateError(''); setForm(p => ({ ...p, [k]: v })) }

  // Duration in ms between start_date and end_date (for multi-day events)
  const durationMs = form.end_date && form.end_date > form.start_date
    ? new Date(form.end_date + 'T00:00:00') - new Date(form.start_date + 'T00:00:00')
    : 0

  const recurringDates = useMemo(() =>
    form.recurring && form.start_date && form.recurrenceEnd
      ? generateRecurringDates(form.start_date, form.recurrence, form.recurrenceEnd)
      : [],
    [form.recurring, form.start_date, form.recurrence, form.recurrenceEnd]
  )

  const handleSave = async () => {
    if (!form.title || !form.start_date) return
    if (form.end_date && form.end_date < form.start_date) {
      setDateError('End date must be on or after the start date')
      return
    }
    setLoading(true)
    const { recurring, recurrence, recurrenceEnd, ...base } = form
    const recurrenceGroup = recurring ? `${form.start_date}-${form.recurrence}-${Date.now()}` : undefined

    if (recurring && recurrenceEnd && recurringDates.length > 0) {
      await Promise.all(recurringDates.map(start => {
        const end = durationMs > 0
          ? new Date(new Date(start + 'T00:00:00').getTime() + durationMs).toISOString().slice(0, 10)
          : start
        return onSave({ ...base, start_date: start, end_date: end, recurrenceGroup })
      }))
    } else {
      await onSave({ ...base, end_date: form.end_date || form.start_date })
    }
    setLoading(false)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-6 flex flex-col gap-4 animate-slide-up max-h-[90vh] overflow-y-auto"
        role="dialog" aria-modal="true" aria-label="Add calendar event"
        onClick={e => e.stopPropagation()}>
        <div className="w-12 h-1 rounded-full bg-slate-200 mx-auto -mt-2 mb-1" />
        <h3 className="font-bold text-slate-800 text-base">Add Calendar Event</h3>

        <Input label="Event title" value={form.title} onChange={e => set('title', e.target.value)}
          placeholder="e.g. Week exchange — pick-up" required />

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-2">Event type</label>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(EVENT_TYPES).map(([val, cfg]) => (
              <button key={val} onClick={() => set('type', val)}
                className={`text-sm px-2 py-2.5 rounded-xl font-medium border-2 text-left transition-all ${
                  form.type === val ? cfg.badge + ' border-current' : 'bg-white dark:bg-slate-700 text-slate-700 border-slate-300'}`}>
                {cfg.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Start date" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} required />
          <Input label="Start time" type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="End date (optional)" type="date" value={form.end_date}
            onChange={e => set('end_date', e.target.value)} min={form.start_date || undefined} />
          <Input label="End time (optional)" type="time" value={form.end_time}
            onChange={e => set('end_time', e.target.value)} />
        </div>
        {dateError && <p className="text-xs text-red-600 font-medium">{dateError}</p>}

        {/* Recurring toggle */}
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.recurring}
              onChange={e => set('recurring', e.target.checked)}
              className="w-4 h-4 rounded accent-brand-600" />
            <span className="text-sm font-semibold text-slate-700">Recurring schedule</span>
          </label>

          {form.recurring && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 flex flex-col gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-2">Frequency</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {RECURRENCE_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => set('recurrence', opt.value)}
                      className={`text-xs px-2 py-2 rounded-lg font-medium border transition-all ${
                        form.recurrence === opt.value
                          ? 'bg-brand-700 text-white border-brand-700'
                          : 'bg-white dark:bg-slate-700 text-slate-600 border-slate-200'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <Input label="Repeat until" type="date" value={form.recurrenceEnd}
                onChange={e => set('recurrenceEnd', e.target.value)}
                min={form.start_date || undefined} required />
              {recurringDates.length > 0 && (
                <p className="text-xs text-brand-700 font-medium">
                  {recurringDates.length} occurrence{recurringDates.length !== 1 ? 's' : ''} will be created
                </p>
              )}
            </div>
          )}
        </div>

        <Input label="Location (optional)" value={form.location}
          onChange={e => set('location', e.target.value)} placeholder="Address or place name" />

        {children?.length > 0 && (
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-1">Child (optional)</label>
            <select value={form.child_id} onChange={e => set('child_id', e.target.value)}
              className="input-base text-sm">
              <option value="">All children</option>
              {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="text-sm font-semibold text-slate-700 block mb-2">Visibility</label>
          <div className="flex gap-2">
            {[['shared','🔗 Shared with co-parent'],['private','🔒 Private']].map(([v, lbl]) => (
              <button key={v} onClick={() => set('visibility', v)}
                className={`flex-1 text-sm py-2.5 px-3 rounded-xl font-medium border-2 transition-all ${
                  form.visibility === v ? 'bg-brand-100 text-brand-700 border-brand-500' : 'bg-white dark:bg-slate-700 text-slate-700 border-slate-300'
                }`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading}
            disabled={!form.title || !form.start_date || (form.recurring && !form.recurrenceEnd)}
            onClick={handleSave}>
            {form.recurring && recurringDates.length > 0
              ? `Save ${recurringDates.length} Events`
              : 'Save Event'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Event detail modal ────────────────────────────────────────────────────────
function EventDetailModal({ event, onClose, onDelete, onRequestChange, onReview, userId, children, calendarRequests }) {
  const cfg = EVENT_TYPES[event.type] || EVENT_TYPES.other
  const canDelete = canDeleteEvent(event, userId)
  const child = children?.find(c => c.id === event.child_id)
  const pendingOnThis = (calendarRequests || []).filter(r => r.event_id === event.id && r.owner_id === userId)
  const canRequestChange = event.ownership === 'partner' && event.visibility === 'shared'

  const formatDate = (ymd) => {
    if (!ymd) return ''
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  }

  const handleDelete = () => {
    onDelete(event.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl flex flex-col animate-slide-up max-h-[85vh]"
        role="dialog" aria-modal="true" aria-label="Event details"
        onClick={e => e.stopPropagation()}>
        {/* Drag handle */}
        <div className="flex-shrink-0 pt-4 pb-2 flex justify-center">
          <div className="w-12 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Colour strip */}
        <div className={`mx-6 mb-4 rounded-2xl px-4 py-3 flex items-center gap-3 ${cfg.badge.split(' ')[0]} border ${cfg.badge.split(' ')[0].replace('bg-', 'border-').replace('100', '200')}`}>
          <span className="text-2xl">{cfg.label.split(' ')[0]}</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-800 text-base leading-tight">{event.title}</p>
            <span className={`text-xs font-semibold ${cfg.badge.split(' ')[1]}`}>
              {cfg.label.split(' ').slice(1).join(' ')}
            </span>
          </div>
        </div>

        <div className="overflow-y-auto no-scrollbar px-6 pb-6 flex flex-col gap-4">
          {/* Date / time */}
          <div className="flex flex-col gap-1">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Date &amp; Time</p>
            <p className="text-sm font-semibold text-slate-800">{formatDate(event.start_date)}</p>
            {(event.start_time || event.end_time) && (
              <p className="text-xs text-slate-500">
                {event.start_time ? `at ${event.start_time}` : ''}
                {event.end_time ? ` – ${event.end_time}` : ''}
              </p>
            )}
            {event.end_date && event.end_date !== event.start_date && (
              <p className="text-xs text-slate-500">
                through {formatDate(event.end_date)}
                {event.end_time ? ` at ${event.end_time}` : ''}
              </p>
            )}
          </div>

          {/* Location */}
          {event.location && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Location</p>
              <p className="text-sm text-slate-700 flex items-start gap-1.5">
                <span>📍</span><span>{event.location}</span>
              </p>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Notes</p>
              <p className="text-sm text-slate-700 leading-relaxed">{event.notes}</p>
            </div>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap gap-2">
            {event.ownership === 'partner' && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">👥 Co-parent event</span>
            )}
            {event.recurrenceGroup && (
              <span className="text-xs bg-brand-100 text-brand-700 px-2.5 py-1 rounded-full font-medium">🔁 Recurring</span>
            )}
            {event.visibility === 'private' && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">🔒 Private</span>
            )}
            {event.visibility === 'shared' && (
              <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">🔗 Shared</span>
            )}
            {child && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-medium">👦 {child.name}</span>
            )}
          </div>

          {/* Pending change requests on this event */}
          {pendingOnThis.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-amber-800">
                ⏳ {pendingOnThis.length} change request{pendingOnThis.length > 1 ? 's' : ''} pending
              </p>
              <button className="text-xs font-bold text-amber-700 underline tap-target"
                onClick={() => { onClose(); onReview?.(pendingOnThis[0]) }}>
                Review →
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Close</Button>
            {canRequestChange && (
              <Button variant="primary" size="md" className="flex-1"
                onClick={() => { onClose(); onRequestChange?.(event) }}>
                Request Change
              </Button>
            )}
            {canDelete && (
              <Button variant="danger" size="md" className="flex-1" onClick={handleDelete}>
                Delete Event
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Request Change modal (co-parent proposes edits to partner's event) ────────
function RequestChangeModal({ event, onClose, onSubmit }) {
  const [form, setForm] = useState({
    proposed_title:      event.title,
    proposed_start_date: event.start_date,
    proposed_start_time: event.start_time || '',
    proposed_end_date:   event.end_date   || event.start_date,
    proposed_end_time:   event.end_time   || '',
    proposed_location:   event.location   || '',
    proposed_notes:      event.notes      || '',
    reason: '',
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState(null)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  // Compute which fields differ from original
  const diff = {
    title:      form.proposed_title      !== event.title,
    start_date: form.proposed_start_date !== event.start_date,
    start_time: form.proposed_start_time !== (event.start_time || ''),
    end_date:   form.proposed_end_date   !== (event.end_date || event.start_date),
    end_time:   form.proposed_end_time   !== (event.end_time || ''),
    location:   form.proposed_location   !== (event.location || ''),
    notes:      form.proposed_notes      !== (event.notes    || ''),
  }
  const hasChanges = Object.values(diff).some(Boolean)

  const handleSubmit = async () => {
    if (!form.reason.trim()) { setError('Please explain why you are requesting this change.'); return }
    if (!hasChanges)         { setError('No changes detected — edit at least one field.'); return }
    setLoading(true)
    setError(null)
    try {
      // Only send fields that actually changed
      const proposed = {
        ...(diff.title      && { proposed_title:      form.proposed_title      }),
        ...(diff.start_date && { proposed_start_date: form.proposed_start_date }),
        ...(diff.start_time && { proposed_start_time: form.proposed_start_time || null }),
        ...(diff.end_date   && { proposed_end_date:   form.proposed_end_date   }),
        ...(diff.end_time   && { proposed_end_time:   form.proposed_end_time   || null }),
        ...(diff.location   && { proposed_location:   form.proposed_location   || null }),
        ...(diff.notes      && { proposed_notes:      form.proposed_notes      || null }),
      }
      await onSubmit(event.id, proposed, form.reason.trim())
      onClose()
    } catch (e) {
      setError(e?.message || 'Could not send request. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const cfg = EVENT_TYPES[event.type] || EVENT_TYPES.other

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-4 max-h-[90vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Request schedule change"
        onClick={e => e.stopPropagation()}>
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />

        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Request Schedule Change</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Propose edits to <span className={`font-semibold ${cfg.badge.split(' ')[1]}`}>{event.title}</span> — your co-parent will approve or decline.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Start date" type="date" value={form.proposed_start_date}
            onChange={e => set('proposed_start_date', e.target.value)}
            className={diff.start_date ? 'ring-2 ring-amber-400' : ''} />
          <Input label="Start time" type="time" value={form.proposed_start_time}
            onChange={e => set('proposed_start_time', e.target.value)}
            className={diff.start_time ? 'ring-2 ring-amber-400' : ''} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="End date" type="date" value={form.proposed_end_date}
            min={form.proposed_start_date}
            onChange={e => set('proposed_end_date', e.target.value)}
            className={diff.end_date ? 'ring-2 ring-amber-400' : ''} />
          <Input label="End time" type="time" value={form.proposed_end_time}
            onChange={e => set('proposed_end_time', e.target.value)}
            className={diff.end_time ? 'ring-2 ring-amber-400' : ''} />
        </div>
        <Input label="Location" value={form.proposed_location}
          onChange={e => set('proposed_location', e.target.value)}
          placeholder="Address or place name"
          className={diff.location ? 'ring-2 ring-amber-400' : ''} />
        <Input label="Title" value={form.proposed_title}
          onChange={e => set('proposed_title', e.target.value)}
          className={diff.title ? 'ring-2 ring-amber-400' : ''} />

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Notes
          </label>
          <textarea value={form.proposed_notes}
            onChange={e => set('proposed_notes', e.target.value)}
            rows={2} placeholder="Any additional context..."
            className={`input-base resize-none text-sm ${diff.notes ? 'ring-2 ring-amber-400' : ''}`} />
        </div>

        {hasChanges && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">Changes proposed:</p>
            <div className="flex flex-wrap gap-1">
              {diff.title      && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">Title</span>}
              {diff.start_date && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">Start date</span>}
              {diff.start_time && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">Start time</span>}
              {diff.end_date   && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">End date</span>}
              {diff.end_time   && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">End time</span>}
              {diff.location   && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">Location</span>}
              {diff.notes      && <span className="text-xs bg-amber-100 dark:bg-amber-800/40 text-amber-700 dark:text-amber-200 px-2 py-0.5 rounded-full">Notes</span>}
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 block mb-1.5">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea value={form.reason}
            onChange={e => set('reason', e.target.value)}
            rows={2} placeholder="Briefly explain why you're requesting this change (e.g. doctor's appointment conflicts)"
            className="input-base resize-none text-sm" />
        </div>

        {error && <p className="text-xs text-red-600 font-medium">{error}</p>}

        <div className="flex gap-3">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading}
            disabled={!hasChanges || !form.reason.trim()}
            onClick={handleSubmit}>
            Send Request
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Review Request modal (event owner approves or rejects) ────────────────────
function ReviewRequestModal({ req, onClose, onApprove, onReject }) {
  const [loading, setLoading] = useState(null) // 'approve' | 'reject' | null
  const cfg = EVENT_TYPES[req.event_type] || EVENT_TYPES.other

  const proposed = [
    req.proposed_start_date && { label: 'Start date', was: req.event_start_date, now: req.proposed_start_date },
    req.proposed_start_time && { label: 'Start time', was: req.event_start_time || '—', now: req.proposed_start_time },
    req.proposed_end_date   && { label: 'End date',   was: req.event_end_date   || '—', now: req.proposed_end_date   },
    req.proposed_end_time   && { label: 'End time',   was: req.event_end_time   || '—', now: req.proposed_end_time   },
    req.proposed_location != null && { label: 'Location', was: req.event_location || '—', now: req.proposed_location || '(removed)' },
    req.proposed_notes    != null && { label: 'Notes',    was: req.event_notes   || '—', now: req.proposed_notes    || '(removed)' },
    req.proposed_title      && { label: 'Title',     was: req.event_title,              now: req.proposed_title      },
  ].filter(Boolean)

  const act = async (action) => {
    setLoading(action)
    try {
      if (action === 'approve') await onApprove(req.id)
      else                      await onReject(req.id)
      onClose()
    } finally { setLoading(null) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={onClose}>
      <div className="w-full max-w-lg mx-auto bg-white dark:bg-slate-800 rounded-t-3xl p-5 flex flex-col gap-4 max-h-[90vh] overflow-y-auto no-scrollbar animate-slide-up"
        role="dialog" aria-modal="true" aria-label="Review change request"
        onClick={e => e.stopPropagation()}>
        <div className="w-12 h-1 rounded-full bg-slate-200 dark:bg-slate-600 mx-auto -mt-1" />

        <div>
          <h3 className="font-bold text-slate-800 dark:text-slate-100 text-base">Review Change Request</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Your co-parent proposed the following changes to{' '}
            <span className={`font-semibold ${cfg.badge.split(' ')[1]}`}>{req.event_title}</span>
          </p>
        </div>

        {/* Proposed changes table */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
          <div className="bg-slate-50 dark:bg-slate-700 px-3 py-2 border-b border-slate-200 dark:border-slate-600">
            <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Proposed Changes</p>
          </div>
          {proposed.length === 0 ? (
            <p className="text-sm text-slate-500 px-3 py-3">No field changes (reason only).</p>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {proposed.map(p => (
                <div key={p.label} className="px-3 py-2.5 grid grid-cols-3 gap-2 items-start">
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{p.label}</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500 line-through">{p.was}</span>
                  <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">{p.now}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reason */}
        <div className="bg-brand-50 dark:bg-brand-900/20 rounded-xl px-3 py-3">
          <p className="text-xs font-bold text-brand-700 dark:text-brand-300 mb-1">Reason given:</p>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">"{req.reason}"</p>
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose}>Later</Button>
          <Button variant="danger" size="md" className="flex-1"
            loading={loading === 'reject'} disabled={!!loading}
            onClick={() => act('reject')}>
            Decline
          </Button>
          <Button variant="primary" size="md" className="flex-1"
            loading={loading === 'approve'} disabled={!!loading}
            onClick={() => act('approve')}>
            Approve
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Request list card ─────────────────────────────────────────────────────────
function RequestCard({ req, role, onReview, onCancel }) {
  const [cancelling, setCancelling] = useState(false)
  const cfg = EVENT_TYPES[req.event_type] || EVENT_TYPES.other

  const handleCancel = async () => {
    setCancelling(true)
    try { await onCancel?.() } finally { setCancelling(false) }
  }

  return (
    <div className="card flex flex-col gap-2 animate-slide-up">
      <div className="flex items-start gap-2">
        <span className="text-xl flex-shrink-0">{cfg.label.split(' ')[0]}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm leading-tight">{req.event_title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{req.event_start_date}</p>
        </div>
        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold flex-shrink-0">Pending</span>
      </div>
      <p className="text-xs text-slate-600 italic bg-slate-50 rounded-lg px-2.5 py-2">"{req.reason}"</p>
      {role === 'owner' && (
        <Button variant="primary" size="sm" className="w-full" onClick={onReview}>
          Review Changes →
        </Button>
      )}
      {role === 'requester' && (
        <Button variant="ghost" size="sm" className="w-full !text-red-600 hover:!bg-red-50"
          loading={cancelling} onClick={handleCancel}>
          Cancel Request
        </Button>
      )}
    </div>
  )
}

// ── Calendar screen ───────────────────────────────────────────────────────────
export default function Calendar() {
  const {
    calendarEvents, addCalendarEvent, deleteCalendarEvent, refreshCalendar,
    calendarRequests, createCalendarRequest, approveCalendarRequest,
    rejectCalendarRequest, cancelCalendarRequest, refreshCalendarRequests,
    coParent, children, user,
  } = useApp()

  const today = new Date()
  const [currentDate, setCurrentDate]       = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [selectedDay, setSelectedDay]       = useState(toYMD(today))
  const [viewMode, setViewMode]             = useState('month')  // 'month' | 'upcoming' | 'requests'
  const [showAdd, setShowAdd]               = useState(false)
  const [detailEvent, setDetailEvent]       = useState(null)
  const [isRefreshing, setIsRefreshing]     = useState(false)
  const [showChangeRequest, setShowChangeRequest] = useState(null) // event to request change on
  const [reviewRequest, setReviewRequest]         = useState(null) // request to review

  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const calDays = useMemo(() => getCalendarDays(year, month), [year, month])

  // Index events by date (YYYY-MM-DD), expanding multi-day ranges so every
  // day in a custody week / multi-day event gets shaded.
  const eventsByDate = useMemo(() => {
    const map = {}
    for (const e of calendarEvents) {
      const start = new Date(e.start_date + 'T00:00:00')
      const end   = new Date((e.end_date || e.start_date) + 'T00:00:00')
      for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const ymd = toYMD(d)
        if (!map[ymd]) map[ymd] = []
        map[ymd].push(e)
      }
    }
    return map
  }, [calendarEvents])

  const selectedEvents = eventsByDate[selectedDay] || []

  // Upcoming events (today + future), sorted
  const upcomingEvents = useMemo(() => {
    const todayStr = toYMD(today)
    return calendarEvents
      .filter(e => e.start_date >= todayStr)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 30)
  }, [calendarEvents])

  // Pending incoming requests (others requesting changes to my events)
  const pendingIncoming = useMemo(() =>
    calendarRequests.filter(r => r.owner_id === user?.id),
    [calendarRequests, user?.id]
  )
  // Set of event IDs that have pending incoming requests (for cell indicators)
  const pendingEventIds = useMemo(() =>
    new Set(pendingIncoming.map(r => r.event_id)),
    [pendingIncoming]
  )
  // Outgoing requests I sent
  const pendingOutgoing = useMemo(() =>
    calendarRequests.filter(r => r.requester_id === user?.id),
    [calendarRequests, user?.id]
  )

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1))

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await Promise.all([refreshCalendar(), refreshCalendarRequests()])
    setIsRefreshing(false)
  }

  const handleApprove = async (requestId) => {
    await approveCalendarRequest(requestId)
    await refreshCalendarRequests()
  }

  const handleReject = async (requestId) => {
    await rejectCalendarRequest(requestId)
    await refreshCalendarRequests()
  }

  const formatDisplayDate = (ymd) => {
    if (!ymd) return ''
    const [y, m, d] = ymd.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <AppHeader title="Custody Calendar"
        right={
          <div className="flex items-center gap-2">
            <button onClick={() => printCalendar({ userName: user?.name, state: user?.state, events: calendarEvents })}
              className="tap-target text-white/80" title="Export PDF"
              aria-label="Export calendar PDF">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
            </button>
            <button onClick={handleRefresh}
              className={`tap-target text-white/80 ${isRefreshing ? 'animate-spin' : ''}`} title="Refresh"
              aria-label="Refresh calendar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={() => setShowAdd(true)}
              className="tap-target w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white/80"
              aria-label="Add event">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        }
      />

      {/* Co-parent sync banner */}
      {coParent && (
        <div className="px-4 py-2 bg-green-50 border-b border-green-200 flex items-center gap-2">
          <span className="text-xs text-green-600">🔗 Synced with</span>
          <span className="text-xs font-bold text-green-700">{coParent.partner_name || coParent.partner_email}</span>
        </div>
      )}

      {/* View toggle */}
      <div className="flex border-b border-slate-100 flex-shrink-0 bg-white dark:bg-slate-900" role="tablist" aria-label="Calendar views">
        {[['month','📅 Month'],['upcoming','📋 Upcoming'],['requests','🔔 Requests']].map(([id, lbl]) => (
          <button key={id} onClick={() => setViewMode(id)}
            role="tab"
            aria-selected={viewMode === id}
            className={`flex-1 py-3 text-xs font-semibold transition-colors relative ${
              viewMode === id ? 'text-brand-700 border-b-2 border-brand-700' : 'text-slate-500'}`}>
            {lbl}
            {id === 'requests' && pendingIncoming.length > 0 && (
              <span className="absolute top-2 right-2 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {pendingIncoming.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="screen no-scrollbar px-0 py-0">
        <div className="mx-auto w-full max-w-6xl flex flex-col">

        {/* ── Month view ──────────────────────────────────────────────────── */}
        {viewMode === 'month' && (
          <div className="lg:grid lg:grid-cols-12 lg:gap-5 lg:px-4 lg:py-4">
            <div className="dark:bg-slate-800 lg:col-span-8 lg:bg-white lg:rounded-2xl lg:border lg:border-slate-200 lg:overflow-hidden">
              {/* Month navigation */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white dark:bg-slate-800">
                <button onClick={prevMonth} className="tap-target text-brand-700 hover:text-brand-800" aria-label="Previous month">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="font-bold text-slate-800">{MONTHS[month]} {year}</span>
                <button onClick={nextMonth} className="tap-target text-brand-700 hover:text-brand-800" aria-label="Next month">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 w-full border-b border-slate-100 bg-white dark:bg-slate-800">
                {DAYS_OF_WEEK.map(d => (
                  <div key={d} className="py-2 text-center text-xs font-bold text-slate-500 uppercase">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 w-full flex-shrink-0 bg-white dark:bg-slate-800">
                {calDays.map((day, i) => {
                  if (!day) return <div key={`pad-${i}`} className="border-r border-b border-slate-50 min-h-[52px]" />
                  const ymd = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  const dayEvents = eventsByDate[ymd] || []
                  const isToday    = ymd === toYMD(today)
                  const isSelected = ymd === selectedDay
                  const hasPending = dayEvents.some(e => pendingEventIds.has(e.id))
                  return (
                    <button key={ymd} onClick={() => setSelectedDay(ymd)}
                      aria-label={`View events for ${new Date(year, month, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                      className={[
                        'border-r border-b border-slate-100 min-h-[52px] p-1 flex flex-col items-center gap-0.5 transition-colors relative',
                        isSelected
                          ? 'ring-2 ring-inset ring-brand-400 ' + (dayEvents.length > 0 ? (EVENT_TYPES[dayEvents[0].type]?.cell || 'bg-slate-50') : 'bg-brand-50')
                          : dayEvents.length > 0
                            ? (EVENT_TYPES[dayEvents[0].type]?.cell || 'bg-slate-50') + ' hover:brightness-95'
                            : 'hover:bg-slate-50',
                      ].join(' ')}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
                        isToday
                          ? 'bg-brand-700 text-white'
                          : isSelected
                          ? 'text-brand-700 font-bold'
                          : dayEvents.length > 0
                          ? 'text-slate-800 font-bold'
                          : 'text-slate-700'}`}>
                        {day}
                      </span>
                      {/* Type dots */}
                      {dayEvents.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-0.5">
                          {[...new Map(dayEvents.map(e => [e.type, e])).values()].slice(0, 3).map((e, j) => (
                            <span key={j}
                              className={`w-1.5 h-1.5 rounded-full ${EVENT_TYPES[e.type]?.dot || 'bg-slate-400'}`} />
                          ))}
                        </div>
                      )}
                      {/* Pending change request indicator */}
                      {hasPending && (
                        <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-amber-400 rounded-full border border-white" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Selected day events */}
            <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 lg:col-span-4 lg:px-0 lg:py-0">
              <div className="flex items-center justify-between lg:px-1">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                  {formatDisplayDate(selectedDay)}
                </p>
                <button onClick={() => setShowAdd(true)}
                  className="text-xs text-brand-700 font-semibold hover:text-brand-800">
                  + Add event
                </button>
              </div>

              {selectedEvents.length === 0 ? (
                <div className="text-center py-6 text-slate-500 lg:bg-white lg:border lg:border-slate-200 lg:rounded-2xl">
                  <p className="text-sm">No events</p>
                  <p className="text-xs mt-1">Tap + to add one</p>
                </div>
              ) : (
                selectedEvents.map(evt => (
                  <EventCard key={evt.id} event={evt}
                    onDelete={deleteCalendarEvent} onView={setDetailEvent}
                    userId={user?.id} hasPendingRequest={pendingEventIds.has(evt.id)} />
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Upcoming view ────────────────────────────────────────────────── */}
        {viewMode === 'upcoming' && (
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 grid grid-cols-1 lg:grid-cols-2 gap-3 content-start">
            {upcomingEvents.length === 0 ? (
              <div className="card bg-brand-50 border border-brand-200 text-center py-10 flex flex-col items-center gap-3 lg:col-span-2">
                <span className="text-4xl">📅</span>
                <p className="font-bold text-slate-800">No upcoming events</p>
                <p className="text-xs text-slate-500">Tap + to schedule a custody exchange, appointment, or school event.</p>
                <Button variant="primary" size="md" onClick={() => setShowAdd(true)}>Add First Event</Button>
              </div>
            ) : (
              upcomingEvents.map(evt => (
                <EventCard key={evt.id} event={evt}
                  onDelete={deleteCalendarEvent} onView={setDetailEvent}
                  userId={user?.id} hasPendingRequest={pendingEventIds.has(evt.id)} />
              ))
            )}
          </div>
        )}

        {/* ── Requests view ────────────────────────────────────────────────── */}
        {viewMode === 'requests' && (
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 flex flex-col gap-6">
            {/* Incoming — to review */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">To Review</p>
                {pendingIncoming.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full">
                    {pendingIncoming.length}
                  </span>
                )}
              </div>
              {pendingIncoming.length === 0 ? (
                <div className="card text-center py-6 text-slate-400">
                  <p className="text-sm">No requests to review</p>
                </div>
              ) : (
                pendingIncoming.map(req => (
                  <RequestCard key={req.id} req={req} role="owner"
                    onReview={() => setReviewRequest(req)} />
                ))
              )}
            </div>

            {/* Outgoing — requests I sent */}
            <div className="flex flex-col gap-3">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Sent by Me</p>
              {pendingOutgoing.length === 0 ? (
                <div className="card text-center py-6 text-slate-400">
                  <p className="text-sm">No pending requests sent</p>
                  {coParent && (
                    <p className="text-xs mt-1">Open a shared co-parent event to propose a change</p>
                  )}
                </div>
              ) : (
                pendingOutgoing.map(req => (
                  <RequestCard key={req.id} req={req} role="requester"
                    onCancel={async () => {
                      await cancelCalendarRequest(req.id)
                      await refreshCalendarRequests()
                    }} />
                ))
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Add event legend */}
      <div className="px-4 pb-3 flex flex-wrap gap-2 bg-white dark:bg-slate-900">
        {Object.entries(EVENT_TYPES).map(([val, cfg]) => (
          <span key={val} className="flex items-center gap-1 text-xs text-slate-500">
            <span className={`w-2 h-2 rounded-full ${cfg.dot}`} /> {cfg.label.split(' ').slice(1).join(' ')}
          </span>
        ))}
      </div>

      {showAdd && (
        <AddEventModal
          onClose={() => setShowAdd(false)}
          onSave={addCalendarEvent}
          defaultDate={selectedDay}
          children={children}
        />
      )}

      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onDelete={deleteCalendarEvent}
          onRequestChange={setShowChangeRequest}
          onReview={setReviewRequest}
          userId={user?.id}
          children={children}
          calendarRequests={calendarRequests}
        />
      )}

      {showChangeRequest && (
        <RequestChangeModal
          event={showChangeRequest}
          onClose={() => setShowChangeRequest(null)}
          onSubmit={createCalendarRequest}
        />
      )}

      {reviewRequest && (
        <ReviewRequestModal
          req={reviewRequest}
          onClose={() => setReviewRequest(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  )
}

// ── Event card ────────────────────────────────────────────────────────────────
function EventCard({ event, onDelete, onView, userId, hasPendingRequest }) {
  const cfg = EVENT_TYPES[event.type] || EVENT_TYPES.other
  const canDelete = canDeleteEvent(event, userId)

  return (
    <div className="card flex gap-3 items-start animate-slide-up cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onView?.(event)}
      role="button"
      aria-label={`View details for ${event.title}`}>
      <div className="relative flex-shrink-0 mt-2">
        <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
        {hasPendingRequest && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full border border-white" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-slate-800 text-base">{event.title}</p>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.label.split(' ').slice(1).join(' ')}</span>
          {hasPendingRequest && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">⏳ Change requested</span>
          )}
          {event.ownership === 'partner' && (
            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium">Co-parent</span>
          )}
          {event.recurrenceGroup && (
            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium">🔁 Recurring</span>
          )}
          {event.visibility === 'private' && (
            <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium">🔒 Private</span>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-0.5">
          {event.start_date}{event.start_time ? ` at ${event.start_time}` : ''}
          {event.end_time && !event.end_date ? ` – ${event.end_time}` : ''}
          {event.end_date && event.end_date !== event.start_date ? ` → ${event.end_date}${event.end_time ? ` at ${event.end_time}` : ''}` : ''}
        </p>
        {event.location && <p className="text-sm text-slate-500 mt-0.5">📍 {event.location}</p>}
        {event.notes    && <p className="text-sm text-slate-500 mt-1 leading-relaxed line-clamp-2">{event.notes}</p>}
      </div>
      {canDelete && (
        <button onClick={e => { e.stopPropagation(); onDelete(event.id) }}
          className="tap-target text-slate-300 hover:text-red-400 transition-colors flex-shrink-0"
          aria-label="Delete event">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  )
}
